/**
 * BoardingPassClearance.tsx — Phase 2 Boarding Pass Clearance™ UI
 *
 * Approved artwork applied 2026-07-26.
 * ─────────────────────────────────────────────────────────────────────────────
 * TASK 78 — GOLD STANDARD MOBILE LAYOUT
 * Mobile (≤640 px) is now a completely independent CSS-Grid render with 7
 * fixed rows so the backpack is ALWAYS visible during gameplay.  No scroll
 * required during the Take-With-You interaction flow.
 *
 * Desktop (>640 px) is the EXACT previous markup — byte-for-byte identical.
 * Reliable mobile detection via window.matchMedia (NOT window.innerWidth, which
 * Android Chrome inflates — confirmed root cause in Tasks 74–75).
 * ─────────────────────────────────────────────────────────────────────────────
 * Frozen Phase 2 interfaces (unchanged):
 *   useTurnstile().execute(): Promise<string>
 *   verifyTurnstileToken(token: string): Promise<void>
 *   POST /api/auth/verify-turnstile { token: string }
 *
 * Spec rules (unchanged):
 *   - 12 items: 6 correct (TAKE WITH YOU) | 6 wrong (LEAVE BEHIND)
 *   - Correct → snaps into backpack, counter increments
 *   - Wrong   → shake animation, 1.5 s message
 *   - Completion only when all 6 correct items are packed
 *   - Auto-continues 1 s after completion (Turnstile runs in background)
 *   - Desktop pointer drag + mobile tap-to-select both supported
 */

import { useState, useEffect } from 'react';
import { Lock, Shield, Plane } from 'lucide-react';
import { CORRECT_ITEMS, WRONG_ITEMS, ALL_ITEMS } from './boardingPassItems';
import type { Item } from './boardingPassItems';
import { useBoardingPassGame } from './useBoardingPassGame';
import BoardingPassClearanceMobile from './BoardingPassClearanceMobile';

// Item catalogue and Item type are imported from boardingPassItems.ts
// (shared with BoardingPassClearanceMobile)

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  /** Called after puzzle solved + Turnstile verified. Parent calls signIn/signUp. */
  onVerified: () => void;
  /** Called if Turnstile verification fails after puzzle completion. */
  onError: (msg: string) => void;
  /** Called if the user explicitly closes/cancels the overlay. */
  onClose: () => void;
}

// Phase type is defined in useBoardingPassGame.ts

// ─────────────────────────────────────────────────────────────────────────────
// Small visual helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Six-dot drag handle matching the approved artwork card design */
function DragHandle() {
  return (
    <div style={{
      position: 'absolute', top: 5, left: 5,
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5px',
      pointerEvents: 'none', zIndex: 2,
    }}>
      {[0,1,2,3,4,5].map(i => (
        <div key={i} style={{
          width: 3, height: 3, borderRadius: '50%',
          background: 'rgba(80,120,200,0.45)',
        }} />
      ))}
    </div>
  );
}

/**
 * Item image with emoji fallback if Unsplash is unreachable.
 * imgClass controls the height behaviour:
 *   'wr-bp-item-img' — desktop: fixed 68px height (from CSS class)
 *   'wr-m-item-img'  — mobile take grid: flex:1 so it fills the 1fr cell
 *   'wr-m-sheet-img' — mobile leave-behind sheet: fixed 68px
 */
function ItemImage({ item, imgClass = 'wr-bp-item-img' }: { item: Item; imgClass?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={imgClass}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: item.correct ? 'rgba(20,40,90,0.6)' : 'rgba(60,10,10,0.6)',
        }}
      >
        <span style={{ fontSize: 32, userSelect: 'none' }}>{item.emoji}</span>
      </div>
    );
  }
  return (
    <img
      src={item.imgUrl}
      alt={item.label}
      draggable={false}
      onError={() => setFailed(true)}
      className={imgClass}
      style={{ width: '100%', objectFit: 'cover', display: 'block', userSelect: 'none' }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function BoardingPassClearance({ onVerified, onError, onClose }: Props) {

  // ── Game logic — all state and handlers live in the shared hook ───────── //
  const {
    phase, count, packedDisplay, shakenItem, wrongMsg,
    isDragOver, backpackGlow, draggedId, selectedId,
    dropZoneRef, handlePointerDown, handlePointerMove,
    handlePointerUp, handleBackpackClick,
  } = useBoardingPassGame({ onVerified, onError });

  // ── Mobile detection — delegates entirely to the CAPTCHA component ────── //
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width:639px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width:639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Task 80: Delegate mobile to the dedicated CAPTCHA component ─────────── //
  if (isMobile) {
    return <BoardingPassClearanceMobile onVerified={onVerified} onError={onError} onClose={onClose} />;
  }

  // ── Item card visual style (desktop) ─────────────────────────────────────── //
  const cardStyle = (item: Item): React.CSSProperties => ({
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
    background: item.correct
      ? 'linear-gradient(160deg, #0c1830, #070f22)'
      : 'linear-gradient(160deg, #1a0606, #100404)',
    border: packedDisplay.has(item.id)
      ? '2px solid rgba(34,197,94,0.65)'
      : selectedId === item.id
        ? '2px solid rgba(250,204,21,0.9)'
        : item.correct
          ? '1.5px solid rgba(30,70,160,0.5)'
          : '1.5px solid rgba(130,20,20,0.55)',
    boxShadow: selectedId === item.id
      ? '0 0 0 2px rgba(250,204,21,0.25), 0 4px 16px rgba(0,0,0,0.5)'
      : packedDisplay.has(item.id)
        ? '0 0 14px rgba(34,197,94,0.18)'
        : '0 3px 10px rgba(0,0,0,0.45)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: packedDisplay.has(item.id) || phase !== 'puzzle' ? 'default' : 'grab',
    userSelect: 'none',
    touchAction: 'none',
    opacity: packedDisplay.has(item.id) ? 0.32 : draggedId === item.id ? 0.38 : 1,
    transition: 'opacity 0.3s, border-color 0.25s, box-shadow 0.25s',
    animation: shakenItem === item.id ? 'wr-shake 0.65s ease' : 'none',
  });

  // ── Shared drop-zone inner content ───────────────────────────────────────  //
  const dropZoneInner = (
    <>
      {(phase === 'success' || phase === 'verifying') ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, animation: 'wr-badge-pop 0.5s ease' }}>
          <span style={{ fontSize: 30, filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.9))' }}>
            {phase === 'verifying' ? '⟳' : '✓'}
          </span>
          <div style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#22c55e', letterSpacing: '0.1em', textAlign: 'center' }}>
            {phase === 'verifying' ? 'VERIFYING…' : 'BOARDING PASS CLEARED'}
          </div>
        </div>
      ) : (
        <>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={isDragOver ? 'rgba(0,220,200,0.95)' : 'rgba(0,185,170,0.55)'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <div style={{
            fontFamily: 'monospace', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.1em',
            color: isDragOver ? 'rgba(0,220,200,0.95)' : selectedId ? '#fbbf24' : 'rgba(0,185,170,0.7)',
            transition: 'color 0.2s',
          }}>
            {isDragOver ? 'DROP HERE' : selectedId ? 'TAP TO PACK' : 'DROP ITEMS HERE'}
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(0,155,140,0.5)' }}>
            {count} / 6 PACKED
          </div>
        </>
      )}
    </>
  );

  // ── Shared drop-zone border/background styles (dynamic) ───────────────── //
  const dropZoneStyle: React.CSSProperties = {
    border: isDragOver
      ? '2.5px dashed rgba(0,220,200,0.95)'
      : (phase === 'success' || phase === 'verifying')
        ? '2.5px solid rgba(34,197,94,0.9)'
        : selectedId
          ? '2.5px dashed rgba(250,204,21,0.8)'
          : '2px dashed rgba(0,200,185,0.42)',
    background: isDragOver
      ? 'rgba(0,210,185,0.08)'
      : (phase === 'success' || phase === 'verifying')
        ? 'rgba(34,197,94,0.07)'
        : 'rgba(0,0,0,0.12)',
    cursor: selectedId ? 'pointer' : 'default',
    animation: (phase === 'puzzle' && count === 0 && !isDragOver)
      ? 'wr-pulse-teal 2.8s ease-in-out infinite'
      : 'none',
    transition: 'border 0.2s, background 0.2s',
  };

  // ── Shared wrong-message overlay ──────────────────────────────────────── //
  const wrongMsgOverlay = wrongMsg ? (
    <div style={{
      position: 'fixed', top: '50%', left: '50%', zIndex: 10001,
      transform: 'translate(-50%, -50%)',
      background: 'rgba(100,22,22,0.97)',
      border: '1.5px solid rgba(239,68,68,0.7)',
      borderRadius: 12, padding: '14px 28px',
      fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
      color: '#fca5a5', pointerEvents: 'none',
      boxShadow: '0 12px 48px rgba(0,0,0,0.7), 0 0 30px rgba(239,68,68,0.25)',
      animation: 'wr-wrong-msg 1.5s ease forwards',
      whiteSpace: 'nowrap',
    }}>
      ✗ &nbsp;{wrongMsg}
    </div>
  ) : null;

  // ── Backpack filter (dynamic, shared) ────────────────────────────────── //
  const backpackFilter = isDragOver
    ? 'drop-shadow(0 0 32px rgba(0,220,190,0.8)) brightness(1.08)'
    : backpackGlow
      ? 'drop-shadow(0 0 26px rgba(34,197,94,0.85)) brightness(1.05)'
      : 'drop-shadow(0 8px 28px rgba(10,80,180,0.45)) brightness(0.92)';

  // ─────────────────────────────────────────────────────────────────────────
  // Shared CSS keyframes + mobile-specific image classes
  // ─────────────────────────────────────────────────────────────────────────
  const sharedStyles = `
    @keyframes wr-shake {
      0%,100%{transform:translateX(0) rotate(0)}
      14%{transform:translateX(-10px) rotate(-2deg)}
      28%{transform:translateX(10px) rotate(2deg)}
      42%{transform:translateX(-8px) rotate(-1.5deg)}
      57%{transform:translateX(8px) rotate(1.5deg)}
      71%{transform:translateX(-4px) rotate(0)}
      85%{transform:translateX(4px) rotate(0)}
    }
    @keyframes wr-glow-green {
      0%,100%{filter:drop-shadow(0 0 18px rgba(34,197,94,0.5))}
      50%{filter:drop-shadow(0 0 40px rgba(34,197,94,0.95))}
    }
    @keyframes wr-snap {
      0%{transform:scale(1.3);opacity:0}
      60%{transform:scale(0.96)}
      100%{transform:scale(1);opacity:1}
    }
    @keyframes wr-pulse-teal {
      0%,100%{border-color:rgba(0,200,180,0.35);box-shadow:inset 0 0 16px rgba(0,180,160,0.04)}
      50%{border-color:rgba(0,220,200,0.65);box-shadow:inset 0 0 28px rgba(0,200,180,0.1),0 0 20px rgba(0,200,180,0.15)}
    }
    @keyframes wr-badge-pop {
      0%{transform:scale(0) rotate(-10deg);opacity:0}
      70%{transform:scale(1.15) rotate(3deg)}
      100%{transform:scale(1) rotate(0);opacity:1}
    }
    @keyframes wr-wrong-msg {
      0%{opacity:0;transform:translate(-50%,-50%) scale(0.8)}
      15%{opacity:1;transform:translate(-50%,-50%) scale(1.02)}
      85%{opacity:1;transform:translate(-50%,-50%) scale(1)}
      100%{opacity:0;transform:translate(-50%,-50%) scale(0.95)}
    }
    /* Mobile image classes — height controlled by CSS so grid rows adapt */
    .wr-m-item-img {
      flex: 1; min-height: 0; width: 100%;
      object-fit: cover; display: block; user-select: none;
    }
    .wr-m-sheet-img {
      height: 68px; width: 100%;
      object-fit: cover; display: block; user-select: none; flex-shrink: 0;
    }
  `;

  // ─────────────────────────────────────────────────────────────────────────
  // DESKTOP RENDER — unchanged from pre-Task-78 approved version
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'linear-gradient(170deg, #060d22 0%, #040a18 45%, #020710 100%)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    }}>

      {/* ── CSS: animations + desktop layout classes ── */}
      <style>{`
        ${sharedStyles}

        /* Main game area — desktop: horizontal flex */
        .wr-bp-area {
          flex:1; display:flex; gap:0;
          padding:7px 10px; min-height:0; overflow:hidden;
        }
        /* Security sidebar */
        .wr-bp-sidebar {
          width:162px; flex-shrink:0;
          display:flex; flex-direction:column; gap:6px;
          padding-right:8px;
        }
        /* Take With You panel */
        .wr-bp-take-panel {
          width:clamp(190px,19vw,222px); flex-shrink:0;
          background:rgba(5,12,32,0.82);
          border:1px solid rgba(25,70,160,0.32);
          border-radius:10px; padding:10px 9px;
          display:flex; flex-direction:column; gap:7px;
          margin-right:8px;
        }
        /* Leave Behind panel */
        .wr-bp-leave-panel {
          width:clamp(190px,19vw,222px); flex-shrink:0;
          background:rgba(14,3,3,0.88);
          border:1px solid rgba(110,18,18,0.42);
          border-radius:10px; padding:10px 9px;
          display:flex; flex-direction:column; gap:7px;
          margin-left:8px;
        }
        /* Panel headers */
        .wr-bp-take-header {
          display:flex; align-items:center; gap:7px;
          padding-bottom:8px;
          border-bottom:1px solid rgba(34,197,94,0.15);
        }
        .wr-bp-leave-header {
          display:flex; align-items:center; gap:7px;
          padding-bottom:8px;
          border-bottom:1px solid rgba(180,30,30,0.2);
        }
        /* Item grids — desktop: 2-col */
        .wr-bp-grid {
          display:grid; grid-template-columns:1fr 1fr;
          gap:6px; flex:1; align-content:start;
        }
        /* Item images */
        .wr-bp-item-img { width:100%; height:68px; object-fit:cover; display:block; user-select:none; flex-shrink:0; }
        /* Backpack column */
        .wr-bp-pack-col {
          flex:1; display:flex; flex-direction:column;
          align-items:center; gap:5px;
          padding:0 8px; min-width:180px;
        }
        /* Backpack image + drop zone container */
        .wr-bp-pack-box {
          flex:1; width:100%; position:relative;
          display:flex; align-items:center; justify-content:center;
          min-height:0; border-radius:12px; overflow:hidden;
        }
        /* Instruction label */
        .wr-bp-pack-label {
          position:absolute; top:5px; left:0; right:0; z-index:4;
          font-family:monospace; font-size:9.5px; font-weight:700;
          letter-spacing:0.15em; color:#00c8c8; text-align:center;
          pointer-events:none;
        }
        /* Backpack hero image */
        .wr-bp-backpack-img {
          position:relative; z-index:2;
          height:88%; width:auto; max-width:92%;
          object-fit:contain; user-select:none;
        }
        /* Drop zone */
        .wr-bp-dropzone {
          position:absolute;
          bottom:8%; left:18%; right:18%; height:44%;
          z-index:3; border-radius:13px;
          display:flex; flex-direction:column;
          align-items:center; justify-content:center; gap:5px;
          transition:border 0.2s, background 0.2s;
        }
        /* Tap hints */
        .wr-bp-taphint {
          text-align:center; padding:4px 6px;
          background:rgba(59,130,246,0.05);
          border:1px solid rgba(59,130,246,0.1);
          border-radius:6px; flex-shrink:0;
        }
        .wr-bp-bottom-strip { display:flex; }
        .wr-bp-footer       { display:flex; }
      `}</style>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* HEADER — logo | SECURITY CHECKPOINT | STEP 2 OF 3                  */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 16px 8px',
        background: 'rgba(2,6,18,0.85)',
        borderBottom: '1px solid rgba(20,60,150,0.2)',
        flexShrink: 0, position: 'relative',
      }}>
        {/* WorkRoamer logo */}
        <img
          src="/workroamer-brand.png" alt="WorkRoamer"
          style={{ height: 30, objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />

        {/* SECURITY CHECKPOINT — centred absolutely */}
        <div style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
        }}>
          <div style={{ width: 38, height: 1, background: 'linear-gradient(90deg,transparent,rgba(0,200,200,0.55))' }} />
          <span style={{
            fontSize: 10.5, color: '#00c8c8',
            fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.22em',
          }}>
            ⊕ SECURITY CHECKPOINT ⊕
          </span>
          <div style={{ width: 38, height: 1, background: 'linear-gradient(90deg,rgba(0,200,200,0.55),transparent)' }} />
        </div>

        {/* STEP 2 OF 3 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          background: 'rgba(10,30,90,0.38)',
          border: '1px solid rgba(59,130,246,0.32)',
          borderRadius: 8, padding: '5px 10px',
        }}>
          <Lock size={12} color="#3b82f6" />
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 7.5, color: '#3b82f6', fontWeight: 700, letterSpacing: '0.15em' }}>STEP 2 OF 3</div>
            <div style={{ fontSize: 8, color: '#3a5070', fontWeight: 600, marginTop: 1 }}>Identity Verification</div>
          </div>
          <div style={{ display: 'flex', gap: 4, marginLeft: 2 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{
                width: 20, height: 4, borderRadius: 2,
                background: i === 1 ? '#3b82f6' : 'rgba(59,130,246,0.14)',
                boxShadow: i === 1 ? '0 0 7px rgba(59,130,246,0.65)' : 'none',
              }} />
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* PAGE TITLE                                                          */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{ textAlign: 'center', padding: '9px 20px 3px', flexShrink: 0 }}>
        <h1 style={{
          margin: 0, lineHeight: 1.1,
          fontSize: 'clamp(19px, 3.1vw, 33px)',
          fontWeight: 900, color: '#dde6f2',
          letterSpacing: '0.07em', textTransform: 'uppercase',
          textShadow: '0 2px 28px rgba(40,100,220,0.22)',
        }}>
          VERIFY YOU'RE TRAVEL-READY
        </h1>
        <p style={{ margin: '5px 0 0', color: '#334a62', fontSize: 11, lineHeight: 1.5 }}>
          Before entering WorkRoamer, complete this quick verification.&nbsp;
          Pack only what a digital nomad would bring.
        </p>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* GAME AREA — desktop 4-column flex row                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="wr-bp-area">

        {/* ── COL 1: Security sidebar ──────────────────────────────────── */}
        <div className="wr-bp-sidebar">
          <div style={{
            flex: 1,
            background: 'rgba(4,10,28,0.92)',
            border: '1px solid rgba(20,60,150,0.28)',
            borderRadius: 10, padding: '10px 12px',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
              <Shield size={13} color="#00c8c8" />
              <span style={{ fontFamily: 'monospace', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.18em', color: '#00c8c8' }}>
                SECURITY CHECK
              </span>
            </div>
            <p style={{ fontSize: 9.5, color: '#263a52', lineHeight: 1.55, margin: '0 0 9px' }}>
              We use this quick check to confirm you're human and help protect your account, data, and workspace.
            </p>
            {[
              { label: 'Bot Protection',     status: 'Active',                           ok: true },
              { label: 'Secure Connection',  status: 'Encrypted',                        ok: true },
              { label: 'Account Protection', status: 'Verified',                         ok: true },
              { label: 'Workspace Access',   status: count >= 6 ? 'Granted' : 'Pending', ok: count >= 6 },
            ].map(({ label, status, ok }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                <Lock size={10} color={ok ? '#22c55e' : '#1c3050'} style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 9, color: '#304560', fontWeight: 600, lineHeight: 1 }}>{label}</div>
                  <div style={{ fontSize: 8, color: ok ? '#22c55e' : '#3a5070', fontWeight: 700, marginTop: 1 }}>{status}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: 'rgba(4,10,28,0.92)', border: '1px solid rgba(20,60,150,0.2)', borderRadius: 10, padding: '9px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
              <Plane size={11} color="#3b82f6" />
              <span style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, letterSpacing: '0.15em', color: '#3b82f6' }}>
                TRAVEL TIP
              </span>
            </div>
            <p style={{ fontSize: 9.5, color: '#263a52', lineHeight: 1.55, margin: 0 }}>
              The best journeys are lighter. Pack smart, travel far, and live free.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '1px 2px' }}>
            <Lock size={8} color="#111e30" />
            <span style={{ fontSize: 7.5, color: '#111e30', fontFamily: 'monospace', letterSpacing: '0.05em', lineHeight: 1.4 }}>
              Your security is our priority.{'\u00A0'}Protected by Cloudflare
            </span>
          </div>
        </div>

        {/* ── TAKE WITH YOU panel ──────────────────────────────────────── */}
        <div className="wr-bp-take-panel">
          <div className="wr-bp-take-header">
            <div style={{
              width: 19, height: 19, borderRadius: '50%',
              background: 'rgba(34,197,94,0.1)', border: '1.5px solid rgba(34,197,94,0.65)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 900, lineHeight: 1 }}>✓</span>
            </div>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', color: '#22c55e' }}>TAKE WITH YOU</div>
              <div style={{ fontSize: 8.5, color: '#263a52', marginTop: 1 }}>Essentials for a digital nomad</div>
            </div>
          </div>

          <div className="wr-bp-grid">
            {CORRECT_ITEMS.map(item => (
              <div
                key={item.id}
                onPointerDown={e => handlePointerDown(e, item.id)}
                onPointerMove={e => handlePointerMove(e, item.id)}
                onPointerUp={e => handlePointerUp(e, item.id)}
                onPointerCancel={e => handlePointerUp(e, item.id)}
                style={{ ...cardStyle(item), flexShrink: 0 }}
              >
                <DragHandle />
                <ItemImage item={item} />
                <div style={{
                  padding: '4px 4px 5px', width: '100%',
                  fontFamily: 'monospace', fontSize: 8, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  color: '#3a5888', textAlign: 'center',
                }}>{item.label}</div>
                {packedDisplay.has(item.id) && (
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: 8,
                    background: 'rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'wr-snap 0.4s ease',
                  }}>
                    <span style={{ fontSize: 26, color: '#22c55e', textShadow: '0 0 14px rgba(34,197,94,0.8)' }}>✓</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop tap hint */}
          <div style={{
            textAlign: 'center', padding: '4px 6px',
            background: 'rgba(59,130,246,0.05)',
            border: '1px solid rgba(59,130,246,0.1)',
            borderRadius: 6, flexShrink: 0,
          }}>
            <span style={{ fontSize: 8, color: '#253548', fontFamily: 'monospace' }}>
              {selectedId
                ? `✦ ${ALL_ITEMS.find(i => i.id === selectedId)?.label} selected — tap backpack to pack`
                : 'Drag items or tap to select, then tap backpack'}
            </span>
          </div>
        </div>

        {/* ── BACKPACK column ─────────────────────────────────────────── */}
        <div className="wr-bp-pack-col">
          <div className="wr-bp-pack-box">
            {/* Airport terminal background */}
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: "url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=900&h=600&fit=crop&auto=format&q=55')",
              backgroundSize: 'cover', backgroundPosition: 'center',
              filter: 'brightness(0.22) saturate(0.7)', zIndex: 0,
            }} />
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse 75% 65% at 50% 40%, rgba(8,25,75,0.55) 0%, rgba(2,6,18,0.85) 100%)',
              zIndex: 1, pointerEvents: 'none',
            }} />

            {/* Instruction label */}
            <div className="wr-bp-pack-label">
              DRAG 6 CORRECT ITEMS INTO THE BACKPACK
            </div>

            {/* Backpack hero image */}
            <img
              src="https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=480&h=560&fit=crop&auto=format&q=80"
              alt="Backpack"
              draggable={false}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              className="wr-bp-backpack-img"
              style={{ filter: backpackFilter, transition: 'filter 0.25s' }}
            />

            {/* Drop zone */}
            <div
              ref={dropZoneRef}
              onClick={handleBackpackClick}
              className="wr-bp-dropzone"
              style={dropZoneStyle}
            >
              {dropZoneInner}
            </div>
          </div>

          {/* Desktop tap hint below backpack */}
          <div style={{
            textAlign: 'center', padding: '4px 6px',
            background: 'rgba(59,130,246,0.05)',
            border: '1px solid rgba(59,130,246,0.1)',
            borderRadius: 6, flexShrink: 0,
          }}>
            <span style={{ fontSize: 8, color: '#253548', fontFamily: 'monospace' }}>
              {selectedId
                ? `✦ ${ALL_ITEMS.find(i => i.id === selectedId)?.label} selected — tap backpack to pack`
                : 'Drag items or tap to select, then tap backpack'}
            </span>
          </div>
        </div>

        {/* ── LEAVE BEHIND panel ──────────────────────────────────────── */}
        <div className="wr-bp-leave-panel">
          <div className="wr-bp-leave-header">
            <div style={{
              width: 19, height: 19, borderRadius: '50%',
              background: 'rgba(239,68,68,0.1)', border: '1.5px solid rgba(239,68,68,0.65)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 900, lineHeight: 1 }}>✕</span>
            </div>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', color: '#ef4444' }}>LEAVE BEHIND</div>
              <div style={{ fontSize: 8.5, color: '#3a1a1a', marginTop: 1 }}>Not needed on your journey</div>
            </div>
          </div>

          <div className="wr-bp-grid">
            {WRONG_ITEMS.map(item => (
              <div
                key={item.id}
                onPointerDown={e => handlePointerDown(e, item.id)}
                onPointerMove={e => handlePointerMove(e, item.id)}
                onPointerUp={e => handlePointerUp(e, item.id)}
                onPointerCancel={e => handlePointerUp(e, item.id)}
                style={{ ...cardStyle(item), flexShrink: 0 }}
              >
                <DragHandle />
                <ItemImage item={item} />
                <div style={{
                  padding: '4px 4px 5px', width: '100%',
                  fontFamily: 'monospace', fontSize: 8, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  color: '#4a2020', textAlign: 'center',
                }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Wrong message */}
      {wrongMsgOverlay}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* BOTTOM STRIP — info | VERIFIED stamp | complete text                */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="wr-bp-bottom-strip" style={{
        flexShrink: 0,
        borderTop: '1px solid rgba(20,50,120,0.22)',
        background: 'rgba(2,6,18,0.75)',
        padding: '8px 16px',
        alignItems: 'center', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <div style={{
            width: 34, height: 34, flexShrink: 0, borderRadius: 6,
            background: 'rgba(15,35,90,0.45)',
            border: '1px solid rgba(59,130,246,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 17 }}>🌍</span>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#8daac4', marginBottom: 2 }}>
              This step helps us verify you're human
            </div>
            <div style={{ fontSize: 9, color: '#223040', lineHeight: 1.45 }}>
              It protects your account, your opportunities, your documents, and your entire WorkRoamer workspace.
            </div>
          </div>
        </div>

        <div style={{
          padding: '6px 13px',
          background: 'rgba(55,15,110,0.32)',
          border: '2px solid rgba(139,92,246,0.62)',
          borderRadius: 8, transform: 'rotate(-3deg)', flexShrink: 0,
          textAlign: 'center',
        }}>
          <div style={{ fontFamily: 'monospace', fontSize: 9.5, fontWeight: 900, letterSpacing: '0.18em', color: '#a78bfa', whiteSpace: 'nowrap' }}>
            ✈ VERIFIED
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 7, color: '#6d28d9', letterSpacing: '0.12em', marginTop: 2 }}>
            TRAVEL-READY
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: count === 6 ? '#22c55e' : '#7a9ab8', transition: 'color 0.3s' }}>
            {count === 6 ? '✓ Complete!' : 'Complete to continue'}
          </div>
          <div style={{ fontSize: 9, color: '#1e3040', marginTop: 2 }}>
            Pack the 6 correct items to show you're ready to roam.
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* FOOTER — step progress + COMPLETE CHECK + tagline                   */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="wr-bp-footer" style={{
        flexShrink: 0,
        background: 'rgba(1,4,14,0.92)',
        borderTop: '1px solid rgba(15,40,80,0.3)',
        padding: '7px 16px',
        alignItems: 'center', gap: 0, flexWrap: 'wrap',
      }}>
        <StepNode n={1} label="START" sub="Welcome" done />
        <StepConnector done />
        <StepNode n={2} label="VERIFY" sub="Pack Your Bag" active />
        <StepConnector />
        <StepNode n={3} label="CONFIRM" sub="Mission Control" locked />

        <div style={{ marginLeft: 'auto', paddingLeft: 14, flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 7,
            background: count === 6
              ? 'linear-gradient(90deg,rgba(18,78,42,0.88),rgba(20,120,58,0.88))'
              : 'rgba(8,15,36,0.82)',
            border: `1px solid ${count === 6 ? 'rgba(34,197,94,0.52)' : 'rgba(20,50,110,0.3)'}`,
            transition: 'all 0.3s',
          }}>
            <Lock size={10} color={count === 6 ? '#22c55e' : '#0e1e30'} />
            <span style={{
              fontFamily: 'monospace', fontSize: 8, fontWeight: 700, letterSpacing: '0.13em',
              color: count === 6 ? '#22c55e' : '#0e1e30',
              whiteSpace: 'nowrap',
            }}>
              {count === 6 ? '✓ COMPLETE CHECK' : 'COMPLETE CHECK'}
            </span>
          </div>
        </div>

        <div style={{
          marginLeft: 14,
          fontFamily: 'monospace', fontSize: 7, color: '#080f1e',
          letterSpacing: '0.18em', whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          SECURE CHECK · SMART TRAVEL · ANYWHERE IN THE WORLD
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step indicator sub-components (visual only — logic unchanged)
// ─────────────────────────────────────────────────────────────────────────────

function StepNode({ n, label, sub, done, active, locked }: {
  n: number; label: string; sub: string;
  done?: boolean; active?: boolean; locked?: boolean;
}) {
  const color  = done ? '#22c55e' : active ? '#3b82f6' : '#0e1e30';
  const bg     = done ? 'rgba(34,197,94,0.14)' : active ? 'rgba(59,130,246,0.14)' : 'rgba(8,14,34,0.65)';
  const border = done ? 'rgba(34,197,94,0.55)' : active ? 'rgba(59,130,246,0.82)' : 'rgba(30,60,120,0.2)';
  const glow   = active ? '0 0 12px rgba(59,130,246,0.42)' : 'none';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        background: bg, border: `2px solid ${border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: glow,
      }}>
        {done
          ? <span style={{ fontSize: 10, color }}>✓</span>
          : locked
            ? <Lock size={8} color={color} />
            : <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color }}>{n}</span>}
      </div>
      <div>
        <div style={{ fontFamily: 'monospace', fontSize: 7.5, fontWeight: 700, color, letterSpacing: '0.1em' }}>{label}</div>
        <div style={{ fontSize: 7, color: active ? '#3a5070' : '#0e1a28' }}>{sub}</div>
      </div>
    </div>
  );
}

function StepConnector({ done }: { done?: boolean }) {
  return (
    <div style={{
      flex: 1, minWidth: 12, maxWidth: 48, height: 2,
      background: done
        ? 'linear-gradient(90deg,rgba(34,197,94,0.5),rgba(59,130,246,0.5))'
        : 'rgba(20,50,100,0.18)',
      margin: '0 8px',
    }} />
  );
}
